/**
 * Role-Based Access Control Context for the Soccer Training Platform
 * Provides role checking, permission validation, and UI component access control
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

// Role definitions matching backend
export const UserRoles = {
  PLATFORM_ADMIN: 'platform_admin',
  CLUB_ADMIN: 'club_admin',
  BRANCH_ADMIN: 'branch_admin',
  COACH: 'coach',
  PARENT: 'parent',
  PLAYER: 'player'
};

// Permission definitions matching backend
export const Permissions = {
  // Platform admin permissions
  PLATFORM_CLUBS_CREATE: 'platform:clubs:create',
  PLATFORM_CLUBS_READ: 'platform:clubs:read',
  PLATFORM_CLUBS_UPDATE: 'platform:clubs:update',
  PLATFORM_CLUBS_DELETE: 'platform:clubs:delete',
  PLATFORM_ADMINS_CREATE: 'platform:admins:create',
  PLATFORM_SYSTEM_MONITOR: 'platform:system:monitor',
  
  // Club admin permissions
  CLUB_USERS_CREATE: 'club:users:create',
  CLUB_USERS_READ: 'club:users:read',
  CLUB_USERS_UPDATE: 'club:users:update',
  CLUB_PROGRAMS_CREATE: 'club:programs:create',
  CLUB_PROGRAMS_READ: 'club:programs:read',
  CLUB_PROGRAMS_UPDATE: 'club:programs:update',
  CLUB_PROGRAMS_DELETE: 'club:programs:delete',
  CLUB_BATCHES_CREATE: 'club:batches:create',
  CLUB_BATCHES_READ: 'club:batches:read',
  CLUB_BATCHES_UPDATE: 'club:batches:update',
  CLUB_REGISTRATIONS_READ: 'club:registrations:read',
  CLUB_REGISTRATIONS_UPDATE: 'club:registrations:update',
  CLUB_REPORTS_READ: 'club:reports:read',
  CLUB_SETTINGS_UPDATE: 'club:settings:update',
  
  // Coach permissions
  COACH_PROGRAMS_READ: 'coach:programs:read',
  COACH_BATCHES_READ: 'coach:batches:read',
  COACH_BATCHES_UPDATE: 'coach:batches:update',
  COACH_PLAYERS_READ: 'coach:players:read',
  COACH_PLAYERS_EVALUATE: 'coach:players:evaluate',
  COACH_REGISTRATIONS_READ: 'coach:registrations:read',
  COACH_REGISTRATIONS_PROCESS: 'coach:registrations:process',
  COACH_ASSESSMENTS_CREATE: 'coach:assessments:create',
  COACH_ASSESSMENTS_READ: 'coach:assessments:read',
  COACH_ASSESSMENTS_UPDATE: 'coach:assessments:update',
  
  // Parent permissions
  PARENT_PROFILE_READ: 'parent:profile:read',
  PARENT_PROFILE_UPDATE: 'parent:profile:update',
  PARENT_CHILDREN_CREATE: 'parent:children:create',
  PARENT_CHILDREN_READ: 'parent:children:read',
  PARENT_CHILDREN_UPDATE: 'parent:children:update',
  PARENT_PROGRAMS_REGISTER: 'parent:programs:register',
  PARENT_PROGRAMS_READ: 'parent:programs:read',
  PARENT_ASSESSMENTS_READ: 'parent:assessments:read',
  
  // Player permissions
  PLAYER_PROFILE_READ: 'player:profile:read',
  PLAYER_PROFILE_UPDATE: 'player:profile:update',
  PLAYER_PROGRAMS_READ: 'player:programs:read',
  PLAYER_ASSESSMENTS_READ: 'player:assessments:read'
};

// Role hierarchy matching backend
const ROLE_HIERARCHY = {
  [UserRoles.PLATFORM_ADMIN]: [UserRoles.CLUB_ADMIN, UserRoles.BRANCH_ADMIN, UserRoles.COACH, UserRoles.PARENT, UserRoles.PLAYER],
  [UserRoles.CLUB_ADMIN]: [UserRoles.BRANCH_ADMIN, UserRoles.COACH, UserRoles.PARENT, UserRoles.PLAYER],
  [UserRoles.BRANCH_ADMIN]: [UserRoles.COACH, UserRoles.PARENT, UserRoles.PLAYER],
  [UserRoles.COACH]: [],
  [UserRoles.PARENT]: [UserRoles.PLAYER],
  [UserRoles.PLAYER]: []
};

// Permission mappings for each role
const ROLE_PERMISSIONS = {
  [UserRoles.PLATFORM_ADMIN]: [
    Permissions.PLATFORM_CLUBS_CREATE,
    Permissions.PLATFORM_CLUBS_READ,
    Permissions.PLATFORM_CLUBS_UPDATE,
    Permissions.PLATFORM_CLUBS_DELETE,
    Permissions.PLATFORM_ADMINS_CREATE,
    Permissions.PLATFORM_SYSTEM_MONITOR
  ],
  [UserRoles.CLUB_ADMIN]: [
    Permissions.CLUB_USERS_CREATE,
    Permissions.CLUB_USERS_READ,
    Permissions.CLUB_USERS_UPDATE,
    Permissions.CLUB_PROGRAMS_CREATE,
    Permissions.CLUB_PROGRAMS_READ,
    Permissions.CLUB_PROGRAMS_UPDATE,
    Permissions.CLUB_PROGRAMS_DELETE,
    Permissions.CLUB_BATCHES_CREATE,
    Permissions.CLUB_BATCHES_READ,
    Permissions.CLUB_BATCHES_UPDATE,
    Permissions.CLUB_REGISTRATIONS_READ,
    Permissions.CLUB_REGISTRATIONS_UPDATE,
    Permissions.CLUB_REPORTS_READ,
    Permissions.CLUB_SETTINGS_UPDATE
  ],
  [UserRoles.BRANCH_ADMIN]: [
    // Branch admins have similar permissions to club admins but scoped to their branch
    Permissions.CLUB_USERS_CREATE,
    Permissions.CLUB_USERS_READ,
    Permissions.CLUB_USERS_UPDATE,
    Permissions.CLUB_PROGRAMS_READ,
    Permissions.CLUB_BATCHES_CREATE,
    Permissions.CLUB_BATCHES_READ,
    Permissions.CLUB_BATCHES_UPDATE,
    Permissions.CLUB_REGISTRATIONS_READ,
    Permissions.CLUB_REGISTRATIONS_UPDATE,
    Permissions.CLUB_REPORTS_READ
  ],
  [UserRoles.COACH]: [
    Permissions.COACH_PROGRAMS_READ,
    Permissions.COACH_BATCHES_READ,
    Permissions.COACH_BATCHES_UPDATE,
    Permissions.COACH_PLAYERS_READ,
    Permissions.COACH_PLAYERS_EVALUATE,
    Permissions.COACH_REGISTRATIONS_READ,
    Permissions.COACH_REGISTRATIONS_PROCESS,
    Permissions.COACH_ASSESSMENTS_CREATE,
    Permissions.COACH_ASSESSMENTS_READ,
    Permissions.COACH_ASSESSMENTS_UPDATE
  ],
  [UserRoles.PARENT]: [
    Permissions.PARENT_PROFILE_READ,
    Permissions.PARENT_PROFILE_UPDATE,
    Permissions.PARENT_CHILDREN_CREATE,
    Permissions.PARENT_CHILDREN_READ,
    Permissions.PARENT_CHILDREN_UPDATE,
    Permissions.PARENT_PROGRAMS_REGISTER,
    Permissions.PARENT_PROGRAMS_READ,
    Permissions.PARENT_ASSESSMENTS_READ
  ],
  [UserRoles.PLAYER]: [
    Permissions.PLAYER_PROFILE_READ,
    Permissions.PLAYER_PROFILE_UPDATE,
    Permissions.PLAYER_PROGRAMS_READ,
    Permissions.PLAYER_ASSESSMENTS_READ
  ]
};

export const RoleProvider = ({ children }) => {
  const authData = useAuth();
  
  // Extract values safely, providing defaults if authData is null/undefined
  const { user, isAuthenticated, loading } = authData || { user: null, isAuthenticated: false, loading: true };
  
  // Basic debugging
  console.log('RoleProvider - Authentication State:', { loading, isAuthenticated, userRole: user?.primary_role, roles: user?.roles });

  const roleUtils = useMemo(() => {
    // Extra safety check - if authData is completely missing, return defaults
    if (!authData) {
      console.error('RoleProvider: useAuth returned undefined/null');
      return {
        loading: true,
        userRole: null,
        hasRole: () => false,
        hasPermission: () => false,
        hasAnyRole: () => false,
        hasAnyPermission: () => false,
        canAccessRole: () => false,
        isPlatformAdmin: false,
        isClubAdmin: false,
        isCoach: false,
        isParent: false,
        isPlayer: false,
        user_name: null,
        user_email: null,
        tenantId: null,
        clubName: null
      };
    }
    console.log('RoleProvider - Role Computation:', {
      loading,
      isAuthenticated,
      userRole: user?.primary_role,
      roles: user?.roles
    });

    // If still loading or not authenticated, return default values
    if (loading || !isAuthenticated || !user) {
      return {
        loading,
        userRole: null,
        hasRole: () => false,
        hasPermission: () => false,
        hasAnyRole: () => false,
        hasAnyPermission: () => false,
        canAccessRole: () => false,
        isPlatformAdmin: false,
        isClubAdmin: false,
        isCoach: false,
        isParent: false,
        isPlayer: false,
        user_name: null,
        user_email: null,
        tenantId: null,
        clubName: null
      };
    }

    // Get user roles (supports both array and single primary_role format)
    const getUserRoles = () => {
      let roles = [];
      
      // Use roles array if it exists (primary from backend)
      if (user.roles && Array.isArray(user.roles)) {
        roles = [...user.roles];
      }
      
      // Also include primary_role if it's a role name (not an ID)
      if (user.primary_role && ['coach', 'branch_admin', 'club_admin', 'parent', 'player', 'platform_admin'].includes(user.primary_role)) {
        if (!roles.includes(user.primary_role)) {
          roles.push(user.primary_role);
        }
      }
      
      return roles.length > 0 ? roles : [];
    };

    const userRoles = getUserRoles();
    const userRole = user.primary_role || userRoles[0] || null; // Primary role from hierarchical system

    // Collect all permissions from all roles
    const userPermissions = userRoles.flatMap(role => ROLE_PERMISSIONS[role] || []);

    // Debug logging
    console.log('🔍 RoleProvider Debug:', {
      user_id: user.id,
      primary_role: user.primary_role,
      location_roles: user.roles?.location_roles,
      detected_roles: userRoles,
      isCoach: userRoles.includes('coach') || userRoles.some(r => r === 'coach')
    });

    console.log('RoleProvider - Authenticated User:', {
      userRoles,
      primary_role: userRole,
      permissionCount: userPermissions.length
    });

    return {
      loading,
      userRole, // Primary role (first in array)
      userRoles, // All roles (array)
      isAuthenticated,
      // Role checking functions (now support multiple roles)
      hasRole: (role) => userRoles.includes(role),
      hasAnyRole: (roles) => roles.some(role => userRoles.includes(role)),
      hasAllRoles: (roles) => roles.every(role => userRoles.includes(role)),
      canAccessRole: (role) => {
        // Check if user has this role directly
        if (userRoles.includes(role)) return true;
        // Check if any of user's roles can access this role
        return userRoles.some(userRole => {
          const subordinateRoles = ROLE_HIERARCHY[userRole] || [];
          return subordinateRoles.includes(role);
        });
      },
      // Permission checking functions
      hasPermission: (permission) => userPermissions.includes(permission),
      hasAnyPermission: (permissions) => 
        permissions.some(permission => userPermissions.includes(permission)),
      hasAllPermissions: (permissions) =>
        permissions.every(permission => userPermissions.includes(permission)),
      // Convenience role flags (check against all roles)
      isPlatformAdmin: userRoles.includes(UserRoles.PLATFORM_ADMIN),
      isClubAdmin: userRoles.includes(UserRoles.CLUB_ADMIN),
      isBranchAdmin: userRoles.includes('branch_admin'),
      isCoach: userRoles.includes(UserRoles.COACH),
      isParent: userRoles.includes(UserRoles.PARENT),
      isPlayer: userRoles.includes(UserRoles.PLAYER),
      // Context-aware helpers (check against all roles)
      canManageClub: () => userRoles.includes(UserRoles.PLATFORM_ADMIN),
      canManagePrograms: () => userRoles.some(role => [UserRoles.CLUB_ADMIN, 'branch_admin'].includes(role)),
      canManageUsers: () => userRoles.some(role => [UserRoles.PLATFORM_ADMIN, UserRoles.CLUB_ADMIN, 'branch_admin'].includes(role)),
      canEvaluatePlayers: () => userRoles.some(role => [UserRoles.COACH, UserRoles.CLUB_ADMIN].includes(role)),
      canViewReports: () => userRoles.some(role => [UserRoles.PLATFORM_ADMIN, UserRoles.CLUB_ADMIN, 'branch_admin', UserRoles.COACH].includes(role)),
      // User context information
      user_name: user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email,
      user_email: user.email,
      club_id: user.club_id,
      clubName: user.club_name
    };
  }, [authData, user, isAuthenticated, loading]);

  // If authData is completely missing, show error state
  if (!authData) {
    return <div>Auth context not available</div>;
  }

  return (
    <RoleContext.Provider value={roleUtils}>
      {children}
    </RoleContext.Provider>
  );
};